/* Interactive gallery customizer — embedded on the Lumetier landing page.
   Lets prospects play with brand color + studio name + layout and watch a fake gallery
   update in real time. Dark Lumetier marketing surface · light inner gallery preview
   (because that's exactly what clients actually see). */

import { useState } from "react";
import { Heart, Layout, Palette, Eye } from "lucide-react";

const GOLD = "#C9A961";

// Fake gallery photos — using Unsplash so the demo always loads beautifully.
const DEMO_PHOTOS = [
  "https://images.unsplash.com/photo-1494774157365-9e04c6720e47?w=400&q=70",
  "https://images.unsplash.com/photo-1519741497674-611481863552?w=400&q=70",
  "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&q=70",
  "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&q=70",
  "https://images.unsplash.com/photo-1525258946800-98cfd641d0de?w=400&q=70",
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=400&q=70",
];

const THEMES = [
  { id: "gold", name: "Gold", color: "#C9A961", desc: "Boutique editorial" },
  { id: "rose", name: "Rose", color: "#B85780", desc: "Romantic & soft" },
  { id: "sage", name: "Sage", color: "#7A8B6F", desc: "Natural & calm" },
  { id: "terra", name: "Terracotta", color: "#C26B4E", desc: "Warm & earthy" },
  { id: "ink", name: "Ink", color: "#1c1917", desc: "Bold & minimal" },
];

const LAYOUTS = [
  { id: "center", name: "Centered", desc: "Classic editorial" },
  { id: "left", name: "Left", desc: "Magazine-style" },
  { id: "minimal", name: "Minimal", desc: "Just the photos" },
];

export default function InteractiveGalleryDemo() {
  const [theme, setTheme] = useState(THEMES[0]);
  const [layout, setLayout] = useState(LAYOUTS[0]);
  const [studioName, setStudioName] = useState("Allure Image");
  const [favorites, setFavorites] = useState({});

  const toggleFav = (i) => setFavorites((p) => ({ ...p, [i]: !p[i] }));
  const favCount = Object.values(favorites).filter(Boolean).length;

  return (
    <section className="border-t border-white/[0.07] py-28" id="customize">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">
            ✦ Make it yours ✦
          </div>
          <h2
            className="text-4xl md:text-5xl font-light leading-tight text-stone-100"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Try the gallery, right here.
          </h2>
          <p className="text-stone-400 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
            Change the color. Change the layout. Type your studio name. Tap a heart. This is exactly what your clients will see.
          </p>
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-5">
          {/* CONTROLS (dark glass) */}
          <div className="space-y-3">
            {/* Studio name input */}
            <div className="bg-white/[0.03] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3 flex items-center gap-1.5">
                <Eye className="w-3 h-3" /> Studio name
              </div>
              <input
                value={studioName}
                onChange={(e) => setStudioName(e.target.value)}
                className="w-full bg-transparent border-b border-white/[0.15] py-2 outline-none focus:border-[#C9A961] text-stone-100 transition-colors placeholder:text-stone-600"
                placeholder="Your studio…"
                data-testid="demo-studio-name"
              />
            </div>

            {/* Theme picker */}
            <div className="bg-white/[0.03] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3 flex items-center gap-1.5">
                <Palette className="w-3 h-3" /> Brand color
              </div>
              <div className="space-y-1.5">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t)}
                    className="w-full flex items-center gap-3 p-2 transition-colors text-left"
                    style={{ background: theme.id === t.id ? "rgba(255,255,255,0.06)" : "transparent" }}
                    data-testid={`demo-theme-${t.id}`}
                  >
                    <div className="w-6 h-6 shrink-0" style={{ background: t.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-200">{t.name}</div>
                      <div className="text-[10px] text-stone-500">{t.desc}</div>
                    </div>
                    {theme.id === t.id && (
                      <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: t.color }}>
                        Active
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Layout picker */}
            <div className="bg-white/[0.03] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3 flex items-center gap-1.5">
                <Layout className="w-3 h-3" /> Cover layout
              </div>
              <div className="space-y-1.5">
                {LAYOUTS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLayout(l)}
                    className="w-full flex items-center justify-between p-2 transition-colors text-left"
                    style={{ background: layout.id === l.id ? "rgba(255,255,255,0.06)" : "transparent" }}
                    data-testid={`demo-layout-${l.id}`}
                  >
                    <div>
                      <div className="text-sm text-stone-200">{l.name}</div>
                      <div className="text-[10px] text-stone-500">{l.desc}</div>
                    </div>
                    {layout.id === l.id && (
                      <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: theme.color }}>
                        ✓
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Live status */}
            <div className="text-[11px] text-stone-500 text-center py-3 border-t border-white/[0.08]">
              {favCount > 0 ? (
                <span style={{ color: theme.color }}>
                  <Heart className="w-3 h-3 inline mb-0.5 fill-current" /> {favCount} favorited
                </span>
              ) : (
                <>Click any photo to favorite it ↓</>
              )}
            </div>
          </div>

          {/* LIVE PREVIEW — stays light (this is what real clients see) */}
          <div className="bg-white border border-white/[0.12] overflow-hidden">
            {/* Cover hero — layout changes based on user choice */}
            <div className="relative h-56 md:h-72 bg-stone-900 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1400&q=70"
                alt=""
                className="w-full h-full object-cover opacity-70"
              />
              {layout.id !== "minimal" && (
                <div
                  className={`absolute inset-0 flex flex-col justify-center px-8 ${
                    layout.id === "left" ? "items-start text-left" : "items-center text-center"
                  }`}
                  style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.4))" }}
                >
                  <div className="text-[10px] uppercase tracking-[0.32em] text-stone-200 mb-2">
                    {studioName || "Your Studio"}
                  </div>
                  <h3
                    className="text-3xl md:text-5xl font-light text-white"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    Adams Family · Spring
                  </h3>
                  <div className="h-px w-12 mt-4" style={{ background: theme.color }} />
                </div>
              )}
            </div>

            {/* Welcome banner with the theme accent */}
            <div
              className="px-6 py-4 text-sm text-center"
              style={{ background: `${theme.color}15`, color: theme.id === "ink" ? "#1c1917" : theme.color }}
            >
              Pick your favorites — your photographer will edit these first.
            </div>

            {/* Photo grid */}
            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DEMO_PHOTOS.map((url, i) => {
                const fav = !!favorites[i];
                return (
                  <button
                    key={i}
                    onClick={() => toggleFav(i)}
                    className="relative aspect-square overflow-hidden group bg-stone-100"
                    data-testid={`demo-photo-${i}`}
                  >
                    <img
                      src={url}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div
                      className="absolute top-1.5 right-1.5 w-8 h-8 flex items-center justify-center transition-all"
                      style={{
                        background: fav ? theme.color : "rgba(255,255,255,0.85)",
                        color: fav ? "#fff" : "#44403c",
                      }}
                    >
                      <Heart className={`w-3.5 h-3.5 ${fav ? "fill-white" : ""}`} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer with live brand stamp */}
            <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">Powered by</div>
                <div className="text-sm" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  {studioName || "Your Studio"} on Lumetier
                </div>
              </div>
              <button
                className="px-4 h-9 text-[10px] uppercase tracking-[0.22em] text-white transition-colors"
                style={{ background: theme.color }}
              >
                Send selections
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-stone-500 mt-8">
          This is the actual Lumetier gallery component — not a screenshot.
          Every studio gets their own customization at signup.
        </p>
      </div>
    </section>
  );
}
